import { Button } from "@mui/material";
import { GridApiCommon } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers";
import { MutableRefObject, memo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { TbCalendarTime, TbUserCancel, TbWalk } from "react-icons/tb";

import CommonTableToolbar from "@/components/CommonTableToolbar";
import ExportTable from "@/components/ExportTable";
import FilterButton from "@/components/table/FilterButton";
import { LocaleStorageKeys } from "@/constants";
import { useExportNewPlanList } from "@/requests/transport.mutations";
import { usePlanActions, usePlanFilters, usePlanIsFullScreen, usePlanSearchItemId } from "@/stores/use-plan-table";
import { useTableDate, useTableDateActions } from "@/stores/use-table-date";
import { ExportTypes } from "@/types/export.types";
import { IPlanPassenger } from "@/types/tasks.types";

import { defaultPlanGridOptions } from ".";
import { CommonFilterSwitch } from "../../types";
import DriverFilter from "./PlanDriverFilter";
import PlanToolbarAirport from "./PlanToolbarAirport";

const newColumnNames = ["transferType", "accommodation", "estimatedPeopleCount"];
const MemoizedToolbar = memo(function CustomToolbar({
    rows,
    reloadTable,
    apiRef,
}: {
    reloadTable: () => void;
    rows: IPlanPassenger<"api">[];
    apiRef: MutableRefObject<GridApiCommon>;
}) {
    const { updateFilters, updateFullScreen } = usePlanActions();
    const filters = usePlanFilters();
    const isFullScreen = usePlanIsFullScreen();
    const searchItemId = usePlanSearchItemId();
    const { t } = useTranslation();
    const { setDate } = useTableDateActions();
    const date = useTableDate();
    const { mutateAsync, isPending: patientPlanExportLoading } = useExportNewPlanList();

    const isDisabled = !!searchItemId;

    const handleDriverChange = useCallback(
        (driverIds: string[]) => {
            updateFilters({
                ...filters,
                selectedDrivers: driverIds,
            });
        },
        [updateFilters, filters]
    );

    const handleCancelClick = useCallback(() => {
        updateFilters(
            {
                ...filters,
                cancelled:
                    filters.cancelled === CommonFilterSwitch.ALL ? CommonFilterSwitch.FALSE : CommonFilterSwitch.ALL,
            },
            apiRef.current
        );
    }, [updateFilters, filters]);
    const handleSelfTransport = useCallback(() => {
        updateFilters(
            {
                ...filters,
                hideSelfTransport: !filters.hideSelfTransport,
            },
            apiRef.current
        );
    }, [updateFilters, filters]);

    const handleExportRequest = async (format: ExportTypes): Promise<ArrayBuffer | undefined> => {
        if (patientPlanExportLoading) return;

        const { airport, isArrival, hideSelfTransport, cancelled, selectedDrivers } = filters;
        const res = await mutateAsync({
            format,
            date,
            cancelled,
            arrival: isArrival,
            airport: airport,
            ids: [],
            hideSelfTransport,
            selectedDrivers,
        });

        return res;
    };
    const getDriverId = useCallback((row: IPlanPassenger<"api">) => row.driverId || "", []);

    return (
        <CommonTableToolbar
            apiRef={apiRef}
            isFullScreen={isFullScreen}
            onFullScreenToggle={updateFullScreen}
            onRefresh={reloadTable}
            storageKey={LocaleStorageKeys.PLAN_GRID_SETTINGS}
            defaultOptions={defaultPlanGridOptions}
            newColumnNames={newColumnNames}
        >
            {isDisabled ? (
                <PlanSearchItemIdButton />
            ) : (
                <DatePicker
                    className="!w-48"
                    value={date}
                    onChange={(date) => {
                        if (date) setDate(date);
                    }}
                />
            )}

            <DriverFilter<IPlanPassenger<"api">>
                allRows={rows}
                currentDrivers={filters.selectedDrivers}
                onDriverChange={handleDriverChange}
                disabled={isDisabled}
                getDriverId={getDriverId}
                driverKey="id"
            />

            <PlanToolbarAirport apiRef={apiRef} rows={rows} disabled={isDisabled} />
            <FilterButton
                handleClick={() => handleCancelClick()}
                icon={TbUserCancel}
                active={filters.cancelled === CommonFilterSwitch.FALSE}
                disabled={isDisabled}
                tooltip={t("tooltips.cancelled", { count: filters.cancelled === CommonFilterSwitch.FALSE ? 2 : 1 })}
            />
            <FilterButton
                handleClick={() => handleSelfTransport()}
                icon={TbWalk}
                active={filters.hideSelfTransport}
                disabled={isDisabled}
                tooltip={t("tooltips.hideSelfTransport", { count: filters.hideSelfTransport ? 2 : 1 })}
            />

            <ExportTable
                fileName="patientPlan"
                filePrefix={() => date.toFormat("dd.MM.yyyy")}
                handleRequest={handleExportRequest}
                loading={patientPlanExportLoading}
                disabled={isDisabled}
            />
        </CommonTableToolbar>
    );
});
function PlanSearchItemIdButton() {
    const searchItemId = usePlanSearchItemId();
    const { updateSearchItemId } = usePlanActions();

    if (!searchItemId) return null;
    return (
        <Button
            className=" grow-0 shrink-0 max-w-fit"
            variant="outlined"
            color="secondary"
            fullWidth
            onClick={() => updateSearchItemId(null)}
            startIcon={<TbCalendarTime />}
        >
            Tarih Formatına Dön
        </Button>
    );
}

export default MemoizedToolbar;
